import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from '@/layouts/AuthLayout';
import { Field, OtpInput, inputClass } from '@/components/forms/form';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { OTP_LENGTH } from '@/lib/config';

const RESEND_SECONDS = 60;

/**
 * Member login. Email OTP — no password, so there is nothing to lose or leak.
 *
 * `createUser: false` matters: logging in must never silently create an
 * account, or a typo in the address quietly makes a second empty one.
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sendCode, verifyCode, session, loading } = useAuth();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const destination = (location.state as { from?: string } | null)?.from ?? '/companions';
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

  // Already signed in — don't show a login form.
  useEffect(() => {
    if (!loading && session) navigate(destination, { replace: true });
  }, [loading, session, navigate, destination]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submitEmail = async () => {
    if (!emailValid || busy) return;
    setBusy(true);
    setError('');
    try {
      await sendCode(email, { createUser: false });
      setStep('code');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (code.length < OTP_LENGTH || busy) return;
    setBusy(true);
    setError('');
    try {
      await verifyCode(email, code);
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    setError('');
    try {
      await sendCode(email, { createUser: false });
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      image="auth-evening.jpg"
      imageAlt="A window seat at dusk with warm light spilling in"
      quote="I did not have anything specific to say. That turned out to be fine."
      attribution="A member, Pune"
    >
      {step === 'email' ? (
        <>
          <h1 className="text-3xl font-semibold">Welcome back</h1>
          <p className="mt-2.5 text-[var(--ink-2)]">
            Enter your email and we will send you a code.
          </p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submitEmail();
            }}
          >
            <Field
              id="email"
              label="Email address"
              error={error}
              hint="We never show your email to anyone you talk to."
            >
              <input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                className={inputClass}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
              />
            </Field>

            <Button size="lg" full type="submit" disabled={!emailValid || busy}>
              {busy ? 'Sending…' : 'Send code'}
            </Button>
          </form>

          <p className="mt-7 text-sm text-[var(--ink-2)]">
            New here?{' '}
            <Link
              to="/signup"
              className="font-medium text-[var(--marigold)] underline-offset-4 hover:underline"
            >
              Create an account
            </Link>
          </p>

          <div className="mt-8 border-t border-[var(--line)] pt-6">
            <p className="text-sm text-[var(--muted)]">
              Here to earn as a companion?{' '}
              <Link
                to="/companion/login"
                className="font-medium text-[var(--ink-2)] underline-offset-4 hover:text-[var(--ink)] hover:underline"
              >
                Companion login
              </Link>
            </p>
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => {
              setStep('email');
              setCode('');
              setError('');
            }}
            className="group mb-6 inline-flex items-center gap-1.5 text-sm text-[var(--ink-2)] transition-colors hover:text-[var(--ink)]"
          >
            <svg
              viewBox="0 0 16 16"
              className="size-3.5 transition-transform duration-200 group-hover:-translate-x-1"
              aria-hidden
            >
              <path
                d="M13 8H4M7.5 4.5L4 8l3.5 3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Change email
          </button>

          <h1 className="text-3xl font-semibold">Enter the code</h1>
          <p className="mt-2.5 text-[var(--ink-2)]">
            Sent to <span className="font-medium text-[var(--ink)]">{email}</span>
          </p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode();
            }}
          >
            <OtpInput value={code} onChange={setCode} />

            {error && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {error}
              </p>
            )}

            <Button size="lg" full type="submit" disabled={code.length < OTP_LENGTH || busy}>
              {busy ? 'Checking…' : 'Verify and continue'}
            </Button>

            <p className="text-center text-sm text-[var(--muted)]">
              {cooldown > 0 ? (
                <>
                  Did not get it? Resend in {Math.floor(cooldown / 60)}:
                  {(cooldown % 60).toString().padStart(2, '0')}
                </>
              ) : (
                <>
                  Did not get it?{' '}
                  <button
                    type="button"
                    onClick={() => void resend()}
                    disabled={busy}
                    className="font-medium text-[var(--marigold)] underline-offset-4 hover:underline"
                  >
                    Send another
                  </button>
                </>
              )}
            </p>

            <p className="text-center text-xs text-[var(--muted)]">
              Check your spam folder if it does not arrive within a minute.
            </p>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
